import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Newspaper, Trophy, Flag, RefreshCw,
    ArrowRight, Calendar, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

function extractArticleId(item) {
    if (item.uri) return item.uri;
    try {
        const href = item.links?.[0]?.href || '';
        const segs = href.split('/');
        return segs[segs.length - 1].split('?')[0];
    } catch { return null; }
}

function itemImage(item, rendition = '4x3') {
    return item?.associations?.featureimage?.renditions?.[rendition]?.href || null;
}

function fmtIssued(iso) {
    if (!iso) return '';
    try {
        return format(parseISO(iso), 'd MMM yyyy, HH:mm');
    } catch { return iso; }
}

function fmtOffTime(iso) {
    if (!iso) return '';
    try { return format(parseISO(iso), 'HH:mm'); } catch { return ''; }
}

function fmtRaceDate(iso) {
    if (!iso) return '';
    try {
        const d = parseISO(iso);
        const today = new Date();
        if (d.toDateString() === today.toDateString()) return 'Today';
        return format(d, 'EEE d MMM');
    } catch { return ''; }
}

// Race status classification — mirrors the logic already used on RaceDetailPage.jsx
function raceStatus(race) {
    const s = race?.status || '';
    if (race?.isAbandoned || s.includes('ABANDONED')) return 'abandoned';
    if (s.includes('WEIGHED_IN') || s.includes('RESULT')) return 'finished';
    if (s.includes('RUNNING') || s.includes('GOING_DOWN') || s.includes('GOING_BEHIND')) return 'live';
    return 'upcoming';
}

// ── Social embed processing (identical pattern to the Football Hub — PA
// Content's body markup isn't sport-specific, so the same processing applies) ──

function processArticleBody(html) {
    if (!html) return [];

    let h = html
        .replace(/\\u003C/g, '<').replace(/\\u003E/g, '>')
        .replace(/\\u0026/g, '&').replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"');

    h = h.replace(/<div[^>]*\bid="kaltura_player_[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
    h = h.replace(/<div[^>]*style="[^"]*padding-bottom\s*:\s*\d+(?:\.\d+)?%[^"]*"[^>]*>\s*<\/div>/gi, '');

    h = h.replace(/<div[^>]*style="[^"]*display\s*:\s*inline-block[^"]*"[^>]*>[\s\S]*?<\/div>\s*<script[^>]*streamamg[^>]*><\/script>/gi, '');
    h = h.replace(/<script[^>]*streamamg[^>]*>[\s\S]*?<\/script>/gi, '');

    h = h.replace(/<!--\s*TRACKING START GA\s*-->[\s\S]*?<!--\s*TRACKING END GA\s*-->/gi, '');

    h = h.replace(/<div[^>]*class="[^"]*flourish-embed[^"]*"[^>]*data-src="([^"]+)"[^>]*>[\s\S]*?<\/div>/gi,
        (_, dataSrc) => `<!--FLOURISH:${dataSrc}-->`);

    const segments = [];
    const combinedRe = /<!--\s*EMBED START (Twitter|X|Instagram)\s*-->([\s\S]*?)<!--\s*EMBED END \1\s*-->|<!--FLOURISH:([^>]+?)-->/gi;
    let lastIndex = 0;
    let match;

    while ((match = combinedRe.exec(h)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'html', content: h.slice(lastIndex, match.index) });
        }
        if (match[3] !== undefined) {
            segments.push({ type: 'flourish', dataSrc: match[3] });
        } else {
            const platform = match[1].toLowerCase();
            if (platform === 'twitter' || platform === 'x') {
                segments.push({ type: 'tweet', ...parseTweet(match[2]) });
            } else {
                const urlM = match[2].match(/href="([^"]*instagram\.com[^"]*)"/i);
                segments.push({ type: 'instagram', url: urlM?.[1] || '' });
            }
        }
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < h.length) segments.push({ type: 'html', content: h.slice(lastIndex) });
    return segments.length ? segments : [{ type: 'html', content: h }];
}

function parseTweet(embedHtml) {
    const bqM = embedHtml.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
    if (!bqM) return { text: '', author: '', username: '', date: '', url: '' };
    const inner = bqM[1];

    const paras = [];
    const pRe = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    let m;
    while ((m = pRe.exec(inner)) !== null) paras.push(m[1]);

    const lastPara = paras[paras.length - 1] || '';
    const decodeEntities = (str) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        return txt.value;
    };
    const tweetText = decodeEntities(paras.slice(0, -1).map(p => p.replace(/<[^>]+>/g, '')).join(' ').trim());

    const urlM = lastPara.match(/href="(https?:\/\/(?:twitter|x)\.com\/[^/]+\/status\/(\d+)[^"]*)">([^<]+)<\/a>/i);
    const url = urlM?.[1] || '';
    const date = urlM?.[3] || '';

    const usernameM = lastPara.match(/@(\w+)/);
    const username = usernameM?.[1] || '';
    const nameM = lastPara.match(/—\s*([^(@<\n]+)/);
    const author = nameM?.[1]?.trim() || username;

    return { text: tweetText, author, username, date, url };
}

function FlourishEmbed({ dataSrc }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!dataSrc || !ref.current) return;
        if (window.Flourish) { window.Flourish.reload?.(); return; }
        if (!document.getElementById('flourish-embed-js')) {
            const s = document.createElement('script');
            s.id = 'flourish-embed-js';
            s.src = 'https://public.flourish.studio/resources/embed.js';
            s.async = true;
            document.head.appendChild(s);
        }
    }, [dataSrc]);
    if (!dataSrc) return null;
    return (
        <div ref={ref} className="flourish-embed" data-src={dataSrc} style={{ width: '100%', minHeight: 400 }} />
    );
}

function TweetCard({ url, text }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!url || !ref.current) return;
        const load = () => window.twttr?.widgets?.load(ref.current);
        if (window.twttr?.widgets) {
            load();
        } else {
            if (!document.getElementById('twitter-widgets-js')) {
                const s = document.createElement('script');
                s.id = 'twitter-widgets-js';
                s.src = 'https://platform.twitter.com/widgets.js';
                s.async = true;
                s.charset = 'utf-8';
                s.onload = load;
                document.head.appendChild(s);
            } else {
                const t = setInterval(() => {
                    if (window.twttr?.widgets) { clearInterval(t); load(); }
                }, 100);
                setTimeout(() => clearInterval(t), 5000);
            }
        }
    }, [url]);

    if (!url) return null;

    return (
        <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center">
            <blockquote className="twitter-tweet" data-theme="dark" data-dnt="true">
                <a href={url}>{text || 'View tweet'}</a>
            </blockquote>
        </div>
    );
}

function InstagramCard({ url }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!url || !ref.current) return;
        const load = () => window.instgrm?.Embeds?.process();
        if (window.instgrm?.Embeds) { load(); return; }
        if (!document.getElementById('instagram-embed-js')) {
            const s = document.createElement('script');
            s.id = 'instagram-embed-js';
            s.src = 'https://www.instagram.com/embed.js';
            s.async = true; s.onload = load;
            document.head.appendChild(s);
        } else {
            const t = setInterval(() => { if (window.instgrm?.Embeds) { clearInterval(t); load(); } }, 100);
            setTimeout(() => clearInterval(t), 5000);
        }
    }, [url]);
    if (!url) return null;
    const embedUrl = url.split('?')[0].replace(/\/?$/, '/');
    return (
        <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center">
            <blockquote
                className="instagram-media"
                data-instgrm-permalink={embedUrl}
                data-instgrm-version="14"
                data-instgrm-captioned
                style={{ background: '#FFF', border: 0, borderRadius: 3, minWidth: 326, width: 'calc(100% - 2px)' }}
            >
                <a href={embedUrl}>View on Instagram</a>
            </blockquote>
        </div>
    );
}

function ArticleBody({ html }) {
    const segments = useMemo(() => processArticleBody(html), [html]);
    return (
        <div>
            {segments.map((seg, i) => {
                if (seg.type === 'tweet') return <TweetCard key={i} {...seg} />;
                if (seg.type === 'instagram') return <InstagramCard key={i} url={seg.url} />;
                if (seg.type === 'flourish') return <FlourishEmbed key={i} dataSrc={seg.dataSrc} />;
                return (
                    <div
                        key={i}
                        className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground leading-relaxed article-body"
                        dangerouslySetInnerHTML={{ __html: seg.content }}
                    />
                );
            })}
        </div>
    );
}

// ── Article Modal ─────────────────────────────────────────────────────────────

function ArticleModal({ articleId, onClose }) {
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!articleId) return;
        setLoading(true);
        setArticle(null);
        axios.get(`${API_URL}/api/racing/hub/article`, {
            params: { item_id: articleId },
            headers: authHeaders(),
        }).then(r => setArticle(r.data)).catch(() => setArticle(null)).finally(() => setLoading(false));
    }, [articleId]);

    const imageUrl = article?.associations?.featureimage?.renditions?.['16x9']?.href
        || article?.associations?.featureimage?.renditions?.['4x3']?.href || null;

    const tags = (article?.subject || [])
        .filter(s => ['sport', 'tag', 'competition'].includes(s.profile))
        .slice(0, 5);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
                    <DialogTitle className="pr-8 text-base font-bold leading-snug uppercase">
                        {loading ? <Skeleton className="h-5 w-3/4" /> : article?.headline}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="p-6 space-y-3">
                        <Skeleton className="h-48 w-full" />
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                    </div>
                ) : !article || article.error ? (
                    <div className="p-6 text-center text-muted-foreground">Could not load article.</div>
                ) : (
                    <div className="p-6 space-y-4">
                        {imageUrl && (
                            <img src={imageUrl} alt={article.headline} className="w-full rounded-lg object-cover max-h-72" />
                        )}
                        <div className="border-b pb-3">
                            {article.byline && <p className="text-sm italic text-muted-foreground">{article.byline}</p>}
                            {article.issued && <p className="text-xs text-muted-foreground mt-0.5">{fmtIssued(article.issued)}</p>}
                        </div>
                        {article.description_text && (
                            <p className="font-semibold text-foreground leading-snug">{article.description_text}</p>
                        )}
                        {(article.body_html || article.body_text) && (
                            <ArticleBody html={article.body_html || `<p>${article.body_text}</p>`} />
                        )}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                                {tags.map(t => (
                                    <Badge key={t.qcode || t.name} variant="secondary" className="text-xs">{t.name}</Badge>
                                ))}
                            </div>
                        )}
                        <div className="pt-2 border-t">
                            <Button variant="outline" className="w-full" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ── News Section ──────────────────────────────────────────────────────────────

function NewsSection({ items, loading, onArticleClick }) {
    if (loading) {
        return (
            <div className="grid md:grid-cols-3 gap-4">
                <Skeleton className="h-80 md:col-span-1" />
                <div className="md:col-span-2 space-y-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    if (!items?.length) {
        return (
            <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                    <Newspaper className="h-7 w-7 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">News coming soon — configure PA_CONTENT_API_KEY to enable.</p>
                </CardContent>
            </Card>
        );
    }

    const [hero, ...rest] = items;
    const secondary = rest.slice(0, 3);
    const grid = rest.slice(3, 7);
    const heroImg = itemImage(hero, '4x3');
    const heroId = extractArticleId(hero);

    return (
        <div className="space-y-4">
            <div className="grid md:grid-cols-5 gap-4">
                <button
                    onClick={() => heroId && onArticleClick(heroId)}
                    className="md:col-span-2 text-left group rounded-xl overflow-hidden border bg-card hover:border-primary/50 transition-colors"
                >
                    {heroImg ? (
                        <div className="aspect-[4/3] overflow-hidden">
                            <img src={heroImg} alt={hero.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                    ) : (
                        <div className="aspect-[4/3] bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                            <Flag className="h-12 w-12 text-primary/30" />
                        </div>
                    )}
                    <div className="p-4">
                        <Badge className="mb-2 text-xs bg-emerald-600 hover:bg-emerald-600 text-white">Racing</Badge>
                        <h2 className="font-bold text-base leading-snug group-hover:text-primary transition-colors line-clamp-3">{hero.headline}</h2>
                        <p className="text-xs text-muted-foreground mt-2">{fmtIssued(hero.issued)}</p>
                    </div>
                </button>

                <div className="md:col-span-3 flex flex-col gap-3">
                    {secondary.map(item => {
                        const img = itemImage(item, '4x3');
                        const id = extractArticleId(item);
                        return (
                            <button
                                key={id || item.headline}
                                onClick={() => id && onArticleClick(id)}
                                className="flex gap-3 text-left group rounded-lg border bg-card p-3 hover:border-primary/50 transition-colors"
                            >
                                {img ? (
                                    <img src={img} alt={item.headline} className="w-24 h-18 object-cover rounded flex-shrink-0" style={{ height: '72px', width: '96px' }} />
                                ) : (
                                    <div className="w-24 h-18 bg-muted rounded flex-shrink-0 flex items-center justify-center" style={{ height: '72px', width: '96px' }}>
                                        <Flag className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <Badge className="mb-1 text-xs bg-emerald-600 hover:bg-emerald-600 text-white">Racing</Badge>
                                    <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">{item.headline}</h3>
                                    <p className="text-xs text-muted-foreground mt-1">{fmtIssued(item.issued)}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {grid.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {grid.map(item => {
                        const img = itemImage(item, '16x9');
                        const id = extractArticleId(item);
                        return (
                            <button
                                key={id || item.headline}
                                onClick={() => id && onArticleClick(id)}
                                className="text-left group rounded-xl overflow-hidden border bg-card hover:border-primary/50 transition-colors"
                            >
                                {img ? (
                                    <div className="aspect-video overflow-hidden">
                                        <img src={img} alt={item.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                    </div>
                                ) : (
                                    <div className="aspect-video bg-muted flex items-center justify-center">
                                        <Flag className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                )}
                                <div className="p-3">
                                    <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">{item.headline}</h3>
                                    <p className="text-xs text-muted-foreground mt-1">{fmtIssued(item.issued)}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Today's Races Carousel ─────────────────────────────────────────────────────

const CARD_W = 176; // px — matches w-44 (11rem = 176px)
const CARD_GAP = 12; // px — gap-3

function RaceCard({ race, anchor }) {
    const navigate = useNavigate();
    const status = raceStatus(race);
    const runners = (race.entrants || []).filter(
        e => !['NON_RUNNER', 'DOUBTFUL', 'WITHDRAWN'].includes(e.status)
    ).length;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/race/${race.id}`, { state: { courseName: race.meeting_name } })}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/race/${race.id}`, { state: { courseName: race.meeting_name } })}
            className={`flex-shrink-0 w-44 rounded-xl border p-3 bg-card transition-colors cursor-pointer select-none
                ${status === 'live' ? 'border-green-500/50 bg-green-500/5' : ''}
                ${status === 'abandoned' ? 'opacity-50' : ''}
                ${anchor ? 'ring-2 ring-primary/40' : 'hover:bg-muted/40'}`}
        >
            <div className="mb-2 flex items-center justify-between gap-1">
                <span className="text-xs text-muted-foreground leading-none truncate">
                    {fmtRaceDate(race.start)}
                    {status === 'upcoming' && race.start ? `, ${fmtOffTime(race.start)}` : ''}
                </span>
                {status === 'live' && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-green-500 flex-shrink-0">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                        Off
                    </span>
                )}
                {status === 'finished' && (
                    <span className="text-xs text-muted-foreground font-medium flex-shrink-0">Result</span>
                )}
                {status === 'abandoned' && (
                    <span className="text-xs text-destructive font-medium flex-shrink-0">Abandoned</span>
                )}
            </div>

            <p className="text-xs font-semibold text-primary/90 truncate mb-1">{race.meeting_name}</p>
            <p className={`text-sm font-medium leading-snug line-clamp-2 mb-2 ${status === 'live' ? 'text-green-500' : ''}`}>
                {race.title}
            </p>

            {runners > 0 && (
                <p className="text-xs text-muted-foreground">{runners} runner{runners !== 1 ? 's' : ''}</p>
            )}
        </div>
    );
}

function RacesCarousel({ races, loading }) {
    const scrollRef = useRef(null);
    const SCROLL_AMOUNT = (CARD_W + CARD_GAP) * 3;

    const scroll = (dir) =>
        scrollRef.current?.scrollBy({ left: dir * SCROLL_AMOUNT, behavior: 'smooth' });

    const sorted = useMemo(() =>
        [...(races || [])].sort((a, b) => (a.start || '').localeCompare(b.start || '')),
    [races]);

    const anchorIdx = useMemo(() => {
        const liveIdx = sorted.map((r, i) => ({ r, i })).filter(({ r }) => raceStatus(r) === 'live').pop()?.i;
        if (liveIdx != null) return liveIdx;

        const nextUpcomingIdx = sorted.findIndex(r => raceStatus(r) === 'upcoming');
        if (nextUpcomingIdx !== -1) return nextUpcomingIdx;

        return 0;
    }, [sorted]);

    useEffect(() => {
        if (!scrollRef.current || !sorted.length) return;
        const el = scrollRef.current;
        const cardOffset = anchorIdx * (CARD_W + CARD_GAP);
        const centreOffset = el.clientWidth / 2 - CARD_W / 2;
        el.scrollLeft = cardOffset - centreOffset;
    }, [sorted.length, anchorIdx]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="flex gap-3 overflow-hidden">
                {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} className="flex-shrink-0 w-44 h-[104px] rounded-xl" />
                ))}
            </div>
        );
    }

    if (!sorted.length) {
        return (
            <Card>
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                    No races found for today.
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="flex-shrink-0 h-8 w-8 rounded-full" onClick={() => scroll(-1)}>
                <ChevronLeft className="h-4 w-4" />
            </Button>

            <div
                ref={scrollRef}
                className="flex gap-3 overflow-x-auto flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {sorted.map((r, i) => (
                    <RaceCard key={r.id} race={r} anchor={i === anchorIdx} />
                ))}
            </div>

            <Button variant="outline" size="icon" className="flex-shrink-0 h-8 w-8 rounded-full" onClick={() => scroll(1)}>
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const RACE_COUNTRIES = ['UK', 'IRE'];

export default function RacingHubPage() {
    const [races, setRaces] = useState([]);
    const [racesLoading, setRacesLoading] = useState(true);
    const [news, setNews] = useState([]);
    const [newsLoading, setNewsLoading] = useState(true);
    const [articleId, setArticleId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchRaces = useCallback(async () => {
        try {
            const responses = await Promise.all(
                RACE_COUNTRIES.map(country =>
                    axios.get(`${API_URL}/api/racing/stages`, { params: { country } }).catch(() => null)
                )
            );
            const flat = [];
            responses.forEach(res => {
                (res?.data?.items || []).forEach(stage => {
                    (stage.events || []).forEach(ev => {
                        flat.push({ ...ev, meeting_name: stage.name, meeting_id: stage.id });
                    });
                });
            });
            setRaces(flat);
        } catch {
            setRaces([]);
        }
    }, []);

    const fetchNews = useCallback(async () => {
        try {
            const r = await axios.get(`${API_URL}/api/racing/hub/news`, { headers: authHeaders() });
            setNews(r.data.items || []);
        } catch {
            setNews([]);
        }
    }, []);

    const fetchAll = useCallback(async () => {
        setRacesLoading(true);
        setNewsLoading(true);
        await Promise.all([
            fetchRaces().finally(() => setRacesLoading(false)),
            fetchNews().finally(() => setNewsLoading(false)),
        ]);
    }, [fetchRaces, fetchNews]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Live poll — refresh the carousel every 30s while a race is off
    const liveCount = races.filter(r => raceStatus(r) === 'live').length;
    useEffect(() => {
        if (!liveCount) return;
        const interval = setInterval(fetchRaces, 30000);
        return () => clearInterval(interval);
    }, [liveCount, fetchRaces]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchAll();
        setRefreshing(false);
    };

    return (
        <div className="container-app py-8 space-y-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Flag className="h-5 w-5 text-primary" />
                        <h1 className="text-3xl font-bold font-heading page-title">Racing Hub</h1>
                        {liveCount > 0 && (
                            <span className="flex items-center gap-1.5 text-xs text-green-400 font-medium ml-2">
                                <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                                {liveCount} off now
                            </span>
                        )}
                    </div>
                    <p className="text-muted-foreground text-sm">Racing news and today's races</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                    <Link to="/races">
                        <Button size="sm" className="gap-1.5">
                            <Calendar className="h-4 w-4" />
                            All Races
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Today's Races */}
            <section>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-5 bg-primary rounded-full" />
                    <h2 className="font-bold text-lg">Today's Races</h2>
                </div>
                <RacesCarousel races={races} loading={racesLoading} />
            </section>

            {/* News */}
            <section>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 bg-primary rounded-full" />
                    <h2 className="font-bold text-lg">Latest News</h2>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground ml-1">
                        <span>powered by</span>
                        <img
                            src="https://explore.pressassociation.com/images/press-association-explore-logo.svg"
                            alt="Press Association"
                            className="h-3.5 w-auto opacity-60 dark:invert"
                        />
                    </div>
                </div>
                <NewsSection items={news} loading={newsLoading} onArticleClick={setArticleId} />
            </section>

            {/* CTA */}
            <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-transparent">
                <CardContent className="py-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-6">
                    <div>
                        <h3 className="text-xl font-bold font-heading mb-1">Fancy backing a winner?</h3>
                        <p className="text-muted-foreground text-sm">Get AI-powered tips and predictions on every race, every day.</p>
                    </div>
                    <Link to="/competitions" className="flex-shrink-0">
                        <Button size="lg" className="gap-2 font-semibold">
                            <Trophy className="h-4 w-4" />
                            View Tips
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </Link>
                </CardContent>
            </Card>

            {/* Article modal */}
            {articleId && <ArticleModal articleId={articleId} onClose={() => setArticleId(null)} />}
        </div>
    );
}
